import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { OCCASIONS } from "@/data/content";
import { easeOutExpo } from "@/lib/motion";

function Tile({ o, className, imgH, onSelect, i }) {
  return (
    <motion.button
      onClick={() => onSelect(o.category)}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: easeOutExpo }}
      className={`group relative overflow-hidden rounded-2xl text-left border border-gold/25 ${className}`}
      data-testid={`occasion-tile-${o.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <img
        src={o.image}
        alt={o.label}
        className={`img-warm absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]`}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(26,21,18,0.82)_0%,rgba(26,21,18,0.25)_45%,rgba(26,21,18,0.05)_100%)] transition-opacity duration-500 group-hover:opacity-95" />
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold-light mb-2">
          {String(i + 1).padStart(2, "0")}
        </span>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold text-ivory leading-tight">
          {o.label}
        </h3>
        <p className={`mt-2 text-sm text-ivory/75 leading-relaxed max-w-sm ${imgH ? "" : "hidden sm:block"}`}>
          {o.desc}
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-gold-light">
          Shop the collection
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </motion.button>
  );
}

export default function Collections({ onSelectFilter }) {
  return (
    <section id="collections" className="py-16 md:py-24 lg:py-32 scroll-mt-16" data-testid="collections-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <motion.div {...{ initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.15 }, transition: { duration: 0.75, ease: easeOutExpo } }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson" data-testid="collections-eyebrow">
              Occasion Collections
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-charcoal" data-testid="collections-heading">
              Every celebration,
              <span className="italic text-terracotta"> beautifully boxed</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.75, delay: 0.1, ease: easeOutExpo }}
            className="max-w-sm text-sm leading-relaxed text-clay"
            data-testid="collections-sub"
          >
            Pick an occasion and we will curate, personalize and hand-pack the
            perfect gift — then send it straight to their doorstep.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <Tile o={OCCASIONS[0]} i={0} className="md:col-span-7 min-h-[380px] md:min-h-[440px]" onSelect={onSelectFilter} />
          <Tile o={OCCASIONS[1]} i={1} className="md:col-span-5 min-h-[380px] md:min-h-[440px]" onSelect={onSelectFilter} />
          <Tile o={OCCASIONS[2]} i={2} className="md:col-span-4 min-h-[320px] md:min-h-[360px]" onSelect={onSelectFilter} />
          <Tile o={OCCASIONS[3]} i={3} className="md:col-span-4 min-h-[320px] md:min-h-[360px]" onSelect={onSelectFilter} />
          <Tile o={OCCASIONS[4]} i={4} className="md:col-span-4 min-h-[320px] md:min-h-[360px]" onSelect={onSelectFilter} />
        </div>
      </div>
    </section>
  );
}
